import { Box, Group, Skeleton, Stack, Text } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';

import type { FormularDFormData } from '../../../types/reports.js';
import { formatTonnage } from '../../../utils/formatting.js';
import { ValidatedNumberInput } from '../../form/validatedFields.js';
import { DECIMAL_INPUT_PROPS } from '../../form/numberFieldPresets.js';
import { PreviousValueForField } from '../../form/PreviousValueLine.js';
import type { CalcStore, ValuesVersionStore } from '../formStores.js';
import { SECTION2_GROUPS } from './catalog.js';
import { FormularDTotal } from './DerivedTotal.js';
import { calculateSection2Total } from './totals.js';

interface FormularDSectionIIProps {
  form: UseFormReturnType<FormularDFormData>;
  store: ValuesVersionStore;
  calcStore: CalcStore;
  disabled?: boolean;
  loading?: boolean;
}

/** Feldbreite einer Klassen-Spalte -- gross genug fuer „123.456,78 t" bei 36px/14px. */
const FIELD_WIDTH = 124;

/**
 * Abschnitt II der Zementartenstatistik (Formular D) -- drei Bloecke: II.a bauaufsichtlich
 * zugelassene Zemente, II.b Putz- und Mauerbinder, II.c hydraulische Tragschichtbinder.
 *
 * Je Block eine eigene Karte statt einer Tabelle (Nutzerabnahme zu Plan 23-09): die drei
 * Bloecke haben KEINE gemeinsame Spaltenachse -- II.a fuehrt Festigkeitsklassen, II.b
 * Moertelklassen, II.c Tragschichtklassen, in 6 / 5 / 4 Spalten. Ein Tabellenlayout
 * behauptet eine Verwandtschaft, die es fachlich nicht gibt, und die wechselnde Spaltenzahl
 * liess die Feldkanten von Block zu Block springen. Gefasste Karten stellen die Frage nicht:
 * jeder Block steht fuer sich. Es ist zugleich die Bauform der CEM-Gruppen aus Abschnitt I
 * (SectionToggleRow variant="card"), nur ohne Auf-/Zuklappen -- bei einer Zeile je Block
 * gaebe es dafuer nichts zu verbergen.
 *
 * D-04 bleibt damit erfuellt: jeder Block zeigt genau seine eigenen Klassen, ausdruecklich
 * NICHT die durchgezogene 9-Spalten-Kopfzeile aus Abschnitt I.
 */
export function FormularDSectionII({ form, store, calcStore, disabled, loading }: FormularDSectionIIProps) {
  return (
    <Stack gap="sm">
      {SECTION2_GROUPS.map((group) => (
        <Box
          key={group.id}
          p="md"
          style={{
            backgroundColor: 'var(--mantine-color-vdzGray-1)',
            borderRadius: 'var(--mantine-radius-md)',
          }}
        >
          {/* Kopf wie bei den CEM-Karten: Titel links, Blocksumme rechts in 18px/700.
              Der frueher als Fussnote gefuehrte Hinweis steht als zweite Zeile direkt
              darunter -- siehe `note` in catalog.ts. */}
          <Group justify="space-between" wrap="nowrap" gap="sm" mb="sm" align="flex-start">
            <div>
              <Text fw={700} size="sm">
                {group.title}
              </Text>
              <Text fz={12} c="vdzGray.6" mt={2} style={{ maxWidth: '75ch' }}>
                {group.note}
              </Text>
            </div>
            <FormularDTotal
              form={form}
              store={store}
              calcStore={calcStore}
              serverKey={group.serverKey}
              compute={(values) => calculateSection2Total(values, group.fields)}
            >
              {(total) => (
                // Einheit immer mitgefuehrt, auch bei leerem Block -- genau wie im Kopf der
                // CEM-Karten. In den Tabellenzeilen von Abschnitt I steht sie dagegen nur an
                // echten Werten; dort ist sie Zeileninhalt, hier Teil der Blocksumme.
                // D-15: der Vorwert steht darunter, in demselben schmalen Behaelter.
                <Box>
                  <Text fz={18} fw={700} style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {formatTonnage(total)} t
                  </Text>
                  <PreviousValueForField field={group.serverKey} />
                </Box>
              )}
            </FormularDTotal>
          </Group>

          {/* Die Klassenbezeichnung steht ueber ihrem Feld statt in einer Kopfzeile --
              ohne Tabelle gibt es keine Spalte, an die sie sich haengen koennte. */}
          <Group gap="sm" wrap="wrap">
            {group.fields.map((field) => (
              <Box key={field.key} w={FIELD_WIDTH}>
                <Text fz={12} fw={700} c="vdzGray.6" ta="center" mb={5}>
                  {field.label}
                </Text>
                {loading ? (
                  <Skeleton height={36} radius="sm" />
                ) : (
                  <ValidatedNumberInput
                    form={form}
                    field={field.key}
                    kind="decimal"
                    disabled={disabled}
                    aria-label={`${group.title} ${field.label}`}
                    suffix=" t"
                    {...DECIMAL_INPUT_PROPS}
                    styles={{ input: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }}
                  />
                )}
              </Box>
            ))}
          </Group>
        </Box>
      ))}
    </Stack>
  );
}
